"use client";
import React, { useMemo, useState, useEffect } from "react";
import { Map, Edit3, Plus, Trash2, X, Search, LayoutList, CheckCircle2, XCircle, RefreshCcw } from "lucide-react";
import { createPortal } from "react-dom";
import { useDistricts } from "../context/DistrictsContext";
import Pagination, { usePagination } from '../shared/Pagination';
import { useStates } from "../context/StatesContext";

export default function Districts() {
  const { districts, fetchDistricts, addDistrict, updateDistrict, deleteDistrict, updateDistrictStatus } = useDistricts();
  const { states } = useStates();

  const [search, setSearch]       = useState("");
  const [sortBy, setSortBy]       = useState("");
  const [filterState, setFilterState] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing]     = useState(null);
  const [name, setName]           = useState("");
  const [selectedState, setSelectedState] = useState("");
  const [status, setStatus]       = useState("active");

  useEffect(() => { fetchDistricts(); }, []);
 

// UPDATE filtered useMemo:
const filtered = useMemo(() => {
  let data = [...districts].filter(d => {
    const matchSearch = d.name.toLowerCase().includes(search.toLowerCase());
    const matchState = filterState ? String(d.state_id) === String(filterState) : true;
    return matchSearch && matchState;
  });
  if (sortBy === "az") data.sort((a, b) => a.name.localeCompare(b.name));
  if (sortBy === "za") data.sort((a, b) => b.name.localeCompare(a.name));
  return data;
}, [districts, search, sortBy, filterState]);

  const openModal = (item = null) => {
    setEditing(item);
    setName(item?.name ?? "");
    setSelectedState(item?.state_id ? String(item.state_id) : "");
    setStatus(item?.status ?? "active");
    setModalOpen(true);
  };
  const { page, pageSize, setPage, setPageSize, totalPages, totalItems, paginated } = usePagination(filtered, 10);

    const closeModal = () => { setModalOpen(false); setEditing(null); setName(""); setSelectedState(""); setStatus("active"); };

  const save = async () => {
    if (!name.trim() || !selectedState) return;
    editing
      ? await updateDistrict(editing.id, { name, state_id: selectedState, status })
      : await addDistrict({ name, state_id: selectedState, status });
    closeModal();
  };

  const toggleStatus = (item) => updateDistrictStatus(item.id, item.status === "active" ? "inactive" : "active");
  const remove = (id) => { if (window.confirm("Delete this district?")) deleteDistrict(id); };

  return (
    <div className="p-4 md:p-6 max-w-[1200px] mx-auto space-y-5">

      {/* STATS */}
      <div className="grid grid-cols-3 gap-4">
        <StatCard label="Total Districts" value={districts.length} icon={LayoutList} accent="border-l-teal-500" color="text-teal-700" />
        <StatCard label="Active" value={districts.filter(d => d.status === "active").length} icon={CheckCircle2} accent="border-l-green-500" color="text-green-600" />
        <StatCard label="Inactive" value={districts.filter(d => d.status === "inactive").length} icon={XCircle} accent="border-l-red-400" color="text-red-500" />
      </div>

      {/* FILTER BAR */}
    {/* FILTER BAR */}
<div className="flex flex-wrap gap-3 bg-white border border-gray-200 rounded-xl px-4 py-3 shadow-sm items-center">
  {/* Search */}
  <div className="relative flex-1 min-w-[180px]">
    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
    <input placeholder="Search districts…" value={search} onChange={e => setSearch(e.target.value)}
      className="w-full pl-9 pr-9 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-400 focus:bg-white transition" />
    {search && (
      <button onClick={() => setSearch("")}
        className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
        <X className="w-3.5 h-3.5" />
      </button>
    )}
  </div>

  {/* State filter */}
  <select value={filterState} onChange={e => setFilterState(e.target.value)}
    className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-400 min-w-[150px]">
    <option value="">All States</option>
    {states.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
  </select>

  {/* Sort */}
  <select value={sortBy} onChange={e => setSortBy(e.target.value)}
    className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-400 min-w-[130px]">
    <option value="">Sort by name</option>
    <option value="az">A → Z</option>
    <option value="za">Z → A</option>
  </select>

  {/* Reset */}
  {(search || filterState || sortBy) && (
    <button
      onClick={() => { setSearch(""); setFilterState(""); setSortBy(""); }}
      className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-gray-400 transition-colors shadow-sm">
      <RefreshCcw className="w-3.5 h-3.5 text-gray-500" /> Reset
    </button>
  )}

  {/* Add button */}
  <button onClick={() => openModal()}
    className="flex items-center gap-2 bg-teal-600 hover:bg-teal-700 text-white px-4 py-2 rounded-xl font-semibold text-sm transition-colors shadow-sm flex-shrink-0 ml-auto">
    <Plus className="w-4 h-4" /><span className="hidden sm:inline">Add District</span>
  </button>
</div>

      {/* DESKTOP TABLE */}
      <div className="hidden md:block bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <table className="min-w-full text-sm">
          <thead className="bg-teal-700 text-white">
            <tr className="bg-teal-700 text-white">
              <Th>ID</Th>
              <Th>State</Th>
              <Th>District Name</Th>
              <Th>Status</Th>
              <Th right>Actions</Th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {filtered.length === 0
              ? <tr><td colSpan={5}><EmptyState label="No districts found" /></td></tr>
              : paginated.map((item, idx) => (
                <tr key={item.id} className={`transition-colors hover:bg-teal-50/40 ${idx % 2 === 0 ? "bg-white" : "bg-gray-50/50"}`}>
                  <Td muted>{idx + 1}</Td>
                  <Td>{item.state_name || "—"}</Td>
                  <Td bold>{item.name}</Td>
                  <Td>
                    <Toggle active={item.status === "active"} onToggle={() => toggleStatus(item)} />
                  </Td>
                  <Td right>
                    <RowActions onEdit={() => openModal(item)} onDelete={() => remove(item.id)} />
                  </Td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
        {totalPages > 1 && (
          <div className="px-4 border-t border-gray-100">
            <Pagination currentPage={page} totalPages={totalPages} totalItems={totalItems} pageSize={pageSize} onPageChange={setPage} onPageSizeChange={setPageSize} />
          </div>
        )}

      {/* MOBILE CARDS */}
      <div className="md:hidden space-y-3">
        {filtered.length === 0
          ? <div className="bg-white rounded-xl p-8 text-center text-gray-400 text-sm border">No districts found</div>
          : paginated.map((item, idx) => (
            <div key={item.id} className={`bg-white rounded-xl border border-gray-100 shadow-sm p-4 space-y-3 ${idx % 2 !== 0 ? "bg-gray-50/40" : ""}`}>
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-400">I{idx + 1}</span>
                <RowActions onEdit={() => openModal(item)} onDelete={() => remove(item.id)} />
              </div>
              <div>
                <p className="font-semibold text-gray-800">{item.name}</p>
                <p className="text-xs text-gray-500 mt-0.5">{item.state_name}</p>
              </div>
              <Toggle active={item.status === "active"} onToggle={() => toggleStatus(item)} />
            </div>
          ))}
      </div>

      {/* MODAL */}
      {modalOpen && createPortal(
        <SlidePanel title={editing ? "Edit District" : "Add District"} onClose={closeModal}>
          <FormField label="State *">
            <select value={selectedState} onChange={e => setSelectedState(e.target.value)}
              className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-white outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm">
              <option value="">Select state…</option>
              {states.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </FormField>
          <FormField label="District Name *">
            <input value={name} onChange={e => setName(e.target.value)} placeholder="Enter district name…" maxLength={50}
              className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-white outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm" />
            <p className="text-xs text-gray-400 mt-1 text-right">{name.length}/50</p>
          </FormField>
          <FormField label="Status">
            <StatusToggleButtons value={status} onChange={setStatus} />
          </FormField>
          <PanelActions onCancel={closeModal} onSave={save} disabled={!name.trim() || !selectedState}
            saveLabel={editing ? "Update District" : "Add District"} />
        </SlidePanel>,
        document.body
      )}
    </div>
  );
}

/* ── Shared primitives ── */
const Th = ({ children, right }) => (
  <th className={`px-5 py-3.5 text-xs font-semibold uppercase tracking-wider ${right ? "text-right" : "text-left"}`}>{children}</th>
);
const Td = ({ children, bold, muted, right }) => (
  <td className={`px-5 py-3.5 ${bold ? "font-medium text-gray-900" : muted ? "text-gray-400 text-xs" : "text-gray-700"} ${right ? "text-right" : ""}`}>{children}</td>
);
const StatCard = ({ label, value, icon: Icon, accent, color }) => (
  <div className={`bg-white rounded-xl border border-gray-100 border-l-4 ${accent} p-4 flex items-center gap-3 shadow-sm`}>
    <div className="w-9 h-9 bg-gray-50 rounded-lg flex items-center justify-center flex-shrink-0">
      <Icon className={`w-4 h-4 ${color}`} />
    </div>
    <div>
      <p className={`text-xl font-bold ${color}`}>{value}</p>
      <p className="text-xs text-gray-500 leading-tight">{label}</p>
    </div>
  </div>
);
const StatusBadge = ({ status }) => (
  <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${status === "active" ? "bg-green-100 text-green-700" : "bg-red-100 text-red-600"}`}>
    <span className={`w-1.5 h-1.5 rounded-full ${status === "active" ? "bg-green-500" : "bg-red-400"}`} />
    {status === "active" ? "Active" : "Inactive"}
  </span>
);
const Toggle = ({ active, onToggle }) => (
  <button onClick={onToggle} className={`relative inline-flex h-5 w-10 flex-shrink-0 items-center rounded-full transition-colors ${active ? "bg-green-500" : "bg-gray-300"}`}>
    <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${active ? "translate-x-5" : "translate-x-0.5"}`} />
  </button>
);
const RowActions = ({ onEdit, onDelete }) => (
  <div className="flex items-center justify-end gap-1">
    <button onClick={onEdit} className="p-1.5 rounded-lg text-teal-600 hover:bg-teal-50 transition-colors"><Edit3 size={15} /></button>
    <button onClick={onDelete} className="p-1.5 rounded-lg text-red-400 hover:bg-red-50 transition-colors"><Trash2 size={15} /></button>
  </div>
);
const EmptyState = ({ label }) => <div className="py-12 text-center text-gray-400 text-sm">{label}</div>;
const SlidePanel = ({ title, onClose, children }) => (
  <div className="fixed inset-0 z-50 flex justify-end">
    <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
    <div className="relative w-full max-w-md h-full bg-white shadow-2xl flex flex-col overflow-hidden">
      <div className="flex-shrink-0 bg-teal-700 px-6 py-5 flex items-center justify-between">
        <h2 className="text-lg font-semibold text-white tracking-tight">{title}</h2>
        <button onClick={onClose} className="w-8 h-8 rounded-lg bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors">
          <X size={16} className="text-white" />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto px-6 py-6 space-y-5">{children}</div>
    </div>
  </div>
);
const FormField = ({ label, required, hint, children }) => (
  <div className="space-y-1.5">
    <label className="block text-sm font-medium text-gray-700">
      {label}{required && <span className="text-red-500 ml-0.5">*</span>}
    </label>
    {children}
    {hint && <p className="text-xs text-gray-400">{hint}</p>}
  </div>
);
const StatusToggleButtons = ({ value, onChange }) => (
  <div className="flex gap-2 p-1 bg-gray-100 rounded-xl">
    {["active", "inactive"].map(s => (
      <button key={s} type="button" onClick={() => onChange(s)}
        className={`flex-1 py-2.5 rounded-lg text-sm font-medium capitalize transition-all ${
          value === s
            ? s === "active" ? "bg-white text-green-700 shadow-sm ring-1 ring-green-200" : "bg-white text-red-600 shadow-sm ring-1 ring-red-200"
            : "text-gray-500 hover:text-gray-700"
        }`}>
        {s}
      </button>
    ))}
  </div>
);
const PanelActions = ({ onCancel, onSave, disabled, saveLabel = "Save" }) => (
  <div className="flex gap-3 pt-4 border-t border-gray-100">
    <button type="button" onClick={onCancel} className="flex-1 py-2.5 rounded-xl border border-gray-200 bg-white text-gray-600 text-sm font-medium hover:bg-gray-50 transition-colors">Cancel</button>
    <button type="button" onClick={onSave} disabled={disabled} className="flex-1 py-2.5 rounded-xl bg-teal-600 text-white text-sm font-semibold hover:bg-teal-700 disabled:opacity-40 transition-colors shadow-sm">{saveLabel}</button>
  </div>
);